import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  RemoveCircle as RemoveCircleIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import restrictionAdminService from '../../services/restrictionAdminService';
import UserSearchField from '../../components/admin/UserSearchField';

// Onglets
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Couleurs statuts
const getStatusColor = (status) => {
  const colors = {
    active: 'error',
    expired: 'default',
    lifted: 'success'
  };
  return colors[status] || 'default';
};

const getBanStatusColor = (status) => {
  const colors = {
    none: 'success',
    temporary: 'warning',
    permanent: 'error'
  };
  return colors[status] || 'default';
};

const UserRestrictionsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const [searchParams, setSearchParams] = useSearchParams();
  
  // État global
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // États Restrictions
  const [restrictions, setRestrictions] = useState([]);
  const [restrictionsPage, setRestrictionsPage] = useState(1);
  const [restrictionsTotalPages, setRestrictionsTotalPages] = useState(1);
  const [restrictionsFilters, setRestrictionsFilters] = useState({
    status: '',
    type: '',
    search: ''
  });
  
  // États Bans
  const [bans, setBans] = useState([]);
  const [bansPage, setBansPage] = useState(1);
  const [bansTotalPages, setBansTotalPages] = useState(1);
  const [bansFilters, setBansFilters] = useState({
    banType: ''
  });
  
  // États Strikes
  const [strikes, setStrikes] = useState([]);
  const [strikesPage, setStrikesPage] = useState(1);
  const [strikesTotalPages, setStrikesTotalPages] = useState(1);
  const [strikesFilters, setStrikesFilters] = useState({
    isActive: '',
    infractionType: ''
  });
  
  // Statistiques
  const [stats, setStats] = useState(null);
  
  // Actions Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUserInfo, setSelectedUserInfo] = useState(null); // 🆕 Info utilisateur (nom, langue, etc.)
  const [restrictionUserInfo, setRestrictionUserInfo] = useState(null); // 🆕 Pour modal "Nouvelle restriction"
  const [banUserInfo, setBanUserInfo] = useState(null); // 🆕 Pour modal "Bannir utilisateur"
  
  // Dialogs
  const [createRestrictionOpen, setCreateRestrictionOpen] = useState(false);
  const [createBanOpen, setCreateBanOpen] = useState(false);
  const [addStrikeOpen, setAddStrikeOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Formulaires
  const [restrictionForm, setRestrictionForm] = useState({
    userId: '',
    restrictionType: '',
    scope: 'user',
    reason: '',
    expiresAt: ''
  });
  
  const [banForm, setBanForm] = useState({
    userId: '',
    banType: 'temporary',
    durationDays: 7,
    reason: ''
  });

  // 🆕 Guard: empêcher de bannir un user déjà banni
  const [banCheck, setBanCheck] = useState({
    loading: false,
    isBanned: false,
    banType: null,
    until: null,
    reason: null,
    error: null
  });
  
  const [strikeForm, setStrikeForm] = useState({
    userId: '',
    infractionType: '',
    reason: '',
    customPoints: ''
  });

  // Chargement des données
  const fetchRestrictions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await restrictionAdminService.listRestrictions({
        page: restrictionsPage,
        limit: 15,
        status: restrictionsFilters.status || undefined,
        type: restrictionsFilters.type || undefined,
        userId: restrictionsFilters.search || undefined
      });
      setRestrictions(response.data || []);
      setRestrictionsTotalPages(response.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [restrictionsPage, restrictionsFilters]);

  const fetchBans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await restrictionAdminService.listBans({
        page: bansPage,
        limit: 15,
        banType: bansFilters.banType || undefined
      });
      setBans(response.data || []);
      setBansTotalPages(response.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bansPage, bansFilters]);

  const fetchStrikes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await restrictionAdminService.listStrikes({
        page: strikesPage,
        limit: 15,
        isActive: strikesFilters.isActive || undefined,
        infractionType: strikesFilters.infractionType || undefined
      });
      setStrikes(response.data || []);
      setStrikesTotalPages(response.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [strikesPage, strikesFilters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await restrictionAdminService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 0) fetchRestrictions();
    else if (activeTab === 1) fetchBans();
    else if (activeTab === 2) fetchStrikes();
  }, [activeTab, fetchRestrictions, fetchBans, fetchStrikes]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (type) => {
    setActionType(type);
    setActionDialogOpen(true);
    handleMenuClose();
    
    // 🆕 Charger les infos de l'utilisateur concerné
    if (selectedItem) {
      const userId = selectedItem.user_id || selectedItem.id;
      if (userId) {
        restrictionAdminService.getUserBasicInfo(userId)
          .then(response => {
            setSelectedUserInfo(response.data);
          })
          .catch(err => {
            console.error('Failed to load user info:', err);
            setSelectedUserInfo(null);
          });
      }
    }
  };

  const handleActionSubmit = async () => {
    if (!actionReason.trim()) return;
    if (!selectedItem) {
      setError('Aucun élément sélectionné');
      return;
    }
    
    setProcessing(true);
    try {
      if (actionType === 'lift_restriction') {
        await restrictionAdminService.liftRestriction(selectedItem.id, actionReason);
        setSuccessMessage('Restriction levée avec succès');
        fetchRestrictions();
      } else if (actionType === 'unban') {
        const userId = selectedItem.user_id || selectedItem.id;
        if (!userId) {
          throw new Error('ID utilisateur manquant');
        }
        await restrictionAdminService.unbanUser(userId, actionReason);
        setSuccessMessage('Utilisateur débanni avec succès');
        fetchBans();
      } else if (actionType === 'void_strike') {
        await restrictionAdminService.voidStrike(selectedItem.id, actionReason);
        setSuccessMessage('Strike annulé avec succès');
        fetchStrikes();
      }
      setActionDialogOpen(false);
      setActionReason('');
      setSelectedItem(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateRestriction = async () => {
    if (!restrictionForm.userId || !restrictionForm.restrictionType || !restrictionForm.reason) return;
    
    setProcessing(true);
    try {
      await restrictionAdminService.createRestriction({
        userId: restrictionForm.userId,
        restrictionType: restrictionForm.restrictionType,
        scope: restrictionForm.scope,
        reason: restrictionForm.reason,
        expiresAt: restrictionForm.expiresAt || undefined
      });
      setSuccessMessage('Restriction créée avec succès');
      setCreateRestrictionOpen(false);
      setRestrictionForm({ userId: '', restrictionType: '', scope: 'user', reason: '', expiresAt: '' });
      setRestrictionUserInfo(null); // 🆕 Reset user info
      fetchRestrictions();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateBan = async () => {
    if (!banForm.userId || !banForm.reason) return;

    // Guard côté UI: ne pas permettre de re-bannir un user déjà banni
    if (banCheck.isBanned) {
      setError('Cet utilisateur est déjà banni. Débannissez-le avant d’appliquer un nouveau ban.');
      return;
    }
    
    setProcessing(true);
    try {
      if (banForm.banType === 'permanent') {
        await restrictionAdminService.createPermanentBan({
          userId: banForm.userId,
          reason: banForm.reason
        });
      } else {
        await restrictionAdminService.createTemporaryBan({
          userId: banForm.userId,
          durationDays: parseInt(banForm.durationDays),
          reason: banForm.reason
        });
      }
      setSuccessMessage('Ban créé avec succès');
      setCreateBanOpen(false);
      setBanForm({ userId: '', banType: 'temporary', durationDays: 7, reason: '' });
      setBanCheck({ loading: false, isBanned: false, banType: null, until: null, reason: null, error: null });
      setBanUserInfo(null); // 🆕 Reset user info
      fetchBans();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleAddStrike = async () => {
    if (!strikeForm.userId || !strikeForm.infractionType || !strikeForm.reason) return;
    
    setProcessing(true);
    try {
      const result = await restrictionAdminService.addStrike({
        userId: strikeForm.userId,
        infractionType: strikeForm.infractionType,
        reason: strikeForm.reason,
        customPoints: strikeForm.customPoints ? parseInt(strikeForm.customPoints) : undefined
      });
      
      let message = 'Strike ajouté avec succès';
      if (result.data?.autoActions?.length > 0) {
        message += ` - Actions automatiques déclenchées: ${result.data.autoActions.length}`;
      }
      setSuccessMessage(message);
      setAddStrikeOpen(false);
      setStrikeForm({ userId: '', infractionType: '', reason: '', customPoints: '' });
      fetchStrikes();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'dd MMM yyyy HH:mm', { locale: fr });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Gestion des Restrictions & Bans
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gérez les restrictions d'accès, bannissements et avertissements utilisateurs
        </Typography>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Statistiques */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Bans temporaires
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.bans?.temporary || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Bans permanents
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.bans?.permanent || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total événements
                </Typography>
                <Typography variant="h4">
                  {stats.events?.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Users trackés
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.bans?.total_users_tracked || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Onglets */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Restrictions" icon={<BlockIcon />} iconPosition="start" />
          <Tab label="Bans" icon={<RemoveCircleIcon />} iconPosition="start" />
          <Tab label="Strikes" icon={<WarningIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Restrictions */}
      <TabPanel value={activeTab} index={0}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={restrictionsFilters.status}
                  label="Statut"
                  onChange={(e) => setRestrictionsFilters({ ...restrictionsFilters, status: e.target.value })}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actives</MenuItem>
                  <MenuItem value="expired">Expirées</MenuItem>
                  <MenuItem value="lifted">Levées</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={restrictionsFilters.type}
                  label="Type"
                  onChange={(e) => setRestrictionsFilters({ ...restrictionsFilters, type: e.target.value })}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {restrictionAdminService.RESTRICTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateRestrictionOpen(true)}
            >
              Nouvelle restriction
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Utilisateur</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Raison</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Expire le</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : restrictions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Aucune restriction trouvée
                  </TableCell>
                </TableRow>
              ) : (
                restrictions.map((restriction) => (
                  <TableRow key={restriction.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">
                            {restriction.user?.email || restriction.user_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {restriction.user?.first_name} {restriction.user?.last_name}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={restrictionAdminService.RESTRICTION_TYPES.find(t => t.value === restriction.restriction_type)?.label || restriction.restriction_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{restriction.scope}</TableCell>
                    <TableCell>
                      <Tooltip title={restriction.reason}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {restriction.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={restriction.status}
                        size="small"
                        color={getStatusColor(restriction.status)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(restriction.expires_at)}</TableCell>
                    <TableCell>{formatDate(restriction.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, restriction)}
                        disabled={restriction.status !== 'active'}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={restrictionsTotalPages}
            page={restrictionsPage}
            onChange={(e, page) => setRestrictionsPage(page)}
          />
        </Box>
      </TabPanel>

      {/* Tab Bans */}
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type de ban</InputLabel>
              <Select
                value={bansFilters.banType}
                label="Type de ban"
                onChange={(e) => setBansFilters({ ...bansFilters, banType: e.target.value })}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="temporary">Temporaire</MenuItem>
                <MenuItem value="permanent">Permanent</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setCreateBanOpen(true)}
            >
              Bannir un utilisateur
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Utilisateur</TableCell>
                <TableCell>Type de ban</TableCell>
                <TableCell>Raison</TableCell>
                <TableCell>Banni le</TableCell>
                <TableCell>Expire le</TableCell>
                <TableCell>Strikes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : bans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Aucun utilisateur banni
                  </TableCell>
                </TableRow>
              ) : (
                bans.map((ban) => (
                  <TableRow key={ban.user_id}>
                    <TableCell>{ban.user_id}</TableCell>
                    <TableCell>
                      <Chip
                        label={ban.ban_status}
                        size="small"
                        color={getBanStatusColor(ban.ban_status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={ban.ban_reason || ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                          {ban.ban_reason || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(ban.banned_at)}</TableCell>
                    <TableCell>
                      {ban.banned_until ? formatDate(ban.banned_until) : 'Permanent'}
                    </TableCell>
                    <TableCell>
                      <Chip label={ban.strike_count || 0} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Débannir">
                        <IconButton
                          onClick={async () => {
                            setSelectedItem(ban);
                            setActionType('unban');
                            setActionDialogOpen(true);
                            
                            // 🆕 Charger les infos utilisateur immédiatement
                            const userId = ban.user_id;
                            if (userId) {
                              try {
                                const response = await restrictionAdminService.getUserBasicInfo(userId);
                                setSelectedUserInfo(response.data);
                              } catch (err) {
                                console.error('Failed to load user info:', err);
                                setSelectedUserInfo(null);
                              }
                            }
                          }}
                          color="success"
                        >
                          <LockOpenIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={bansTotalPages}
            page={bansPage}
            onChange={(e, page) => setBansPage(page)}
          />
        </Box>
      </TabPanel>

      {/* Tab Strikes */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Actif</InputLabel>
                <Select
                  value={strikesFilters.isActive}
                  label="Actif"
                  onChange={(e) => setStrikesFilters({ ...strikesFilters, isActive: e.target.value })}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="true">Actifs</MenuItem>
                  <MenuItem value="false">Annulés/Expirés</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Type infraction</InputLabel>
                <Select
                  value={strikesFilters.infractionType}
                  label="Type infraction"
                  onChange={(e) => setStrikesFilters({ ...strikesFilters, infractionType: e.target.value })}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {restrictionAdminService.INFRACTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Button
              variant="contained"
              color="warning"
              startIcon={<WarningIcon />}
              onClick={() => setAddStrikeOpen(true)}
            >
              Ajouter un strike
            </Button>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Utilisateur</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Raison</TableCell>
                <TableCell>Actif</TableCell>
                <TableCell>Expire le</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : strikes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Aucun strike trouvé
                  </TableCell>
                </TableRow>
              ) : (
                strikes.map((strike) => (
                  <TableRow key={strike.id}>
                    <TableCell>
                      {strike.user?.email || strike.user_id}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={restrictionAdminService.INFRACTION_TYPES.find(t => t.value === strike.infraction_type)?.label || strike.infraction_type}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={strike.strike_points} size="small" color="error" />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={strike.reason}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {strike.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={strike.is_active ? 'Actif' : 'Inactif'}
                        size="small"
                        color={strike.is_active ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(strike.expires_at)}</TableCell>
                    <TableCell>{formatDate(strike.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, strike)}
                        disabled={!strike.is_active}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={strikesTotalPages}
            page={strikesPage}
            onChange={(e, page) => setStrikesPage(page)}
          />
        </Box>
      </TabPanel>

      {/* Menu Actions */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {activeTab === 0 && (
          <MenuItem onClick={() => handleAction('lift_restriction')}>
            <ListItemIcon><CheckCircleIcon /></ListItemIcon>
            <ListItemText>Lever la restriction</ListItemText>
          </MenuItem>
        )}
        {activeTab === 2 && (
          <MenuItem onClick={() => handleAction('void_strike')}>
            <ListItemIcon><CloseIcon /></ListItemIcon>
            <ListItemText>Annuler le strike</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => navigate(`/admin/users/${selectedItem?.user_id || selectedItem?.user?.id}/restrictions`)}>
          <ListItemIcon><HistoryIcon /></ListItemIcon>
          <ListItemText>Voir historique</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog Action (Lift/Void/Unban) */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
          setSelectedItem(null);
          setSelectedUserInfo(null); // 🆕 Reset user info
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === 'lift_restriction' && 'Lever la restriction'}
          {actionType === 'unban' && 'Débannir l\'utilisateur'}
          {actionType === 'void_strike' && 'Annuler le strike'}
        </DialogTitle>
        <DialogContent>
          {/* 🆕 Afficher les infos utilisateur si disponibles */}
          {selectedUserInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Utilisateur:</strong> {selectedUserInfo.name || selectedUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Langue de préférence:</strong> {selectedUserInfo.language.toUpperCase()}
              </Typography>
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Raison"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            placeholder="Expliquez pourquoi cette action est effectuée..."
            sx={{ mt: 2 }}
            helperText={selectedUserInfo ? `Cette raison sera envoyée à l'utilisateur dans sa langue (${selectedUserInfo.language.toUpperCase()})` : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionDialogOpen(false);
              setSelectedItem(null);
              setSelectedUserInfo(null); // 🆕 Reset user info
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleActionSubmit}
            variant="contained"
            disabled={!actionReason.trim() || processing}
          >
            {processing ? <CircularProgress size={24} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Créer Restriction */}
      <Dialog 
        open={createRestrictionOpen} 
        onClose={() => {
          setCreateRestrictionOpen(false);
          setRestrictionUserInfo(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Nouvelle restriction</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <UserSearchField
              value={restrictionForm.userId}
              onChange={async (userId) => {
                setRestrictionForm({ ...restrictionForm, userId });
                
                // 🆕 Charger les infos utilisateur
                if (userId) {
                  try {
                    const response = await restrictionAdminService.getUserBasicInfo(userId);
                    setRestrictionUserInfo(response.data);
                  } catch (err) {
                    console.error('Failed to load user info:', err);
                    setRestrictionUserInfo(null);
                  }
                } else {
                  setRestrictionUserInfo(null);
                }
              }}
              label="Utilisateur"
              placeholder="Rechercher par nom ou email..."
              required
            />
            {/* 🆕 Afficher les infos utilisateur */}
            {restrictionUserInfo && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Utilisateur:</strong> {restrictionUserInfo.name || restrictionUserInfo.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {restrictionUserInfo.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Langue de préférence:</strong> {restrictionUserInfo.language.toUpperCase()}
                </Typography>
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Type de restriction</InputLabel>
              <Select
                value={restrictionForm.restrictionType}
                label="Type de restriction"
                onChange={(e) => setRestrictionForm({ ...restrictionForm, restrictionType: e.target.value })}
              >
                {restrictionAdminService.RESTRICTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select
                value={restrictionForm.scope}
                label="Scope"
                onChange={(e) => setRestrictionForm({ ...restrictionForm, scope: e.target.value })}
              >
                {restrictionAdminService.RESTRICTION_SCOPES.map((scope) => (
                  <MenuItem key={scope.value} value={scope.value}>{scope.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Raison"
              value={restrictionForm.reason}
              onChange={(e) => setRestrictionForm({ ...restrictionForm, reason: e.target.value })}
              helperText={restrictionUserInfo ? `Cette raison sera envoyée à l'utilisateur dans sa langue (${restrictionUserInfo.language.toUpperCase()})` : ''}
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Date d'expiration (optionnel)"
              value={restrictionForm.expiresAt}
              onChange={(e) => setRestrictionForm({ ...restrictionForm, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Laisser vide pour permanent"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateRestrictionOpen(false);
            setRestrictionUserInfo(null);
          }}>Annuler</Button>
          <Button onClick={handleCreateRestriction} variant="contained" disabled={processing}>
            {processing ? <CircularProgress size={24} /> : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Créer Ban */}
      <Dialog
        open={createBanOpen}
        onClose={() => {
          setCreateBanOpen(false);
          setBanCheck({ loading: false, isBanned: false, banType: null, until: null, reason: null, error: null });
          setBanUserInfo(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bannir un utilisateur</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <UserSearchField
              value={banForm.userId}
              onChange={async (userId) => {
                setBanForm({ ...banForm, userId });

                if (!userId) {
                  setBanCheck({ loading: false, isBanned: false, banType: null, until: null, reason: null, error: null });
                  setBanUserInfo(null);
                  return;
                }

                try {
                  setBanCheck(prev => ({ ...prev, loading: true, error: null }));
                  
                  // 🆕 Charger les infos utilisateur ET le statut de ban en parallèle
                  const [summary, userInfo] = await Promise.all([
                    restrictionAdminService.getUserRestrictionSummary(userId),
                    restrictionAdminService.getUserBasicInfo(userId)
                  ]);
                  
                  setBanUserInfo(userInfo.data);
                  
                  const ban = summary?.data?.ban;
                  setBanCheck({
                    loading: false,
                    isBanned: !!ban?.isBanned,
                    banType: ban?.type || null,
                    until: ban?.until || null,
                    reason: ban?.reason || null,
                    error: null
                  });
                } catch (e) {
                  setBanCheck({
                    loading: false,
                    isBanned: false,
                    banType: null,
                    until: null,
                    reason: null,
                    error: e?.message || 'Impossible de vérifier le statut de ban'
                  });
                  setBanUserInfo(null);
                }
              }}
              label="Utilisateur"
              placeholder="Rechercher par nom ou email..."
              required
            />

            {/* 🆕 Afficher les infos utilisateur */}
            {banUserInfo && !banCheck.isBanned && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Utilisateur:</strong> {banUserInfo.name || banUserInfo.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {banUserInfo.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Langue de préférence:</strong> {banUserInfo.language.toUpperCase()}
                </Typography>
              </Alert>
            )}

            {banCheck.error && (
              <Alert severity="warning">{banCheck.error}</Alert>
            )}

            {!banCheck.loading && banCheck.isBanned && (
              <Alert severity="error">
                Cet utilisateur est déjà banni ({banCheck.banType || 'unknown'}).
                {banCheck.until ? ` Fin: ${formatDate(banCheck.until)}.` : ''}
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Type de ban</InputLabel>
              <Select
                value={banForm.banType}
                label="Type de ban"
                onChange={(e) => setBanForm({ ...banForm, banType: e.target.value })}
              >
                <MenuItem value="temporary">Temporaire</MenuItem>
                <MenuItem value="permanent">Permanent</MenuItem>
              </Select>
            </FormControl>
            {banForm.banType === 'temporary' && (
              <TextField
                fullWidth
                type="number"
                label="Durée (jours)"
                value={banForm.durationDays}
                onChange={(e) => setBanForm({ ...banForm, durationDays: e.target.value })}
                inputProps={{ min: 1, max: 365 }}
              />
            )}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Raison"
              value={banForm.reason}
              onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
              helperText={banUserInfo ? `Cette raison sera envoyée à l'utilisateur dans sa langue (${banUserInfo.language.toUpperCase()})` : ''}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateBanOpen(false);
              setBanCheck({ loading: false, isBanned: false, banType: null, until: null, reason: null, error: null });
              setBanUserInfo(null);
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreateBan}
            variant="contained"
            color="error"
            disabled={processing || banCheck.loading || banCheck.isBanned}
          >
            {processing ? <CircularProgress size={24} /> : 'Bannir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ajouter Strike */}
      <Dialog open={addStrikeOpen} onClose={() => setAddStrikeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un strike</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <UserSearchField
              value={strikeForm.userId}
              onChange={(userId) => setStrikeForm({ ...strikeForm, userId })}
              label="Utilisateur"
              placeholder="Rechercher par nom ou email..."
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type d'infraction</InputLabel>
              <Select
                value={strikeForm.infractionType}
                label="Type d'infraction"
                onChange={(e) => setStrikeForm({ ...strikeForm, infractionType: e.target.value })}
              >
                {restrictionAdminService.INFRACTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label} ({type.points} pts)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="number"
              label="Points personnalisés (optionnel)"
              value={strikeForm.customPoints}
              onChange={(e) => setStrikeForm({ ...strikeForm, customPoints: e.target.value })}
              inputProps={{ min: 1, max: 10 }}
              helperText="Par défaut, les points standard du type d'infraction sont utilisés"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Raison"
              value={strikeForm.reason}
              onChange={(e) => setStrikeForm({ ...strikeForm, reason: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStrikeOpen(false)}>Annuler</Button>
          <Button onClick={handleAddStrike} variant="contained" color="warning" disabled={processing}>
            {processing ? <CircularProgress size={24} /> : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserRestrictionsPage;
